import api from './api';

export interface MusicMentionData {
  title: string;
  artistName: string;
  type: 'song' | 'album' | 'artist';
  artworkUrl?: string;
  isrc?: string | null;
  upc?: string | null;
  releaseDate?: string | null;
  appleArtistId?: string | null;
  pillText?: string;
  mediaId?: string | null;
}

export interface SubmitMention {
  mediaId?: string | null;
  title: string;
  artistName: string;
  type: string;
  placeholderIndex: number;
}

/**
 * Build bracket display text from a mention's fields.
 * Artist: [Artist Name]
 * Song/Album: [Title - Artist]
 */
export function buildPillText(m: { type: string; title: string; artistName: string }): string {
  if (m.type === 'artist') {
    return '[' + (m.artistName || m.title) + ']';
  }
  return '[' + m.title + ' - ' + m.artistName + ']';
}

/**
 * Convert {mention:N} tokens in text to bracket display text for editing.
 */
export function tokensToBracketText(
  text: string,
  musicMentions: Array<{ type: string; title: string; artistName: string }>
): string {
  return text.replace(/\{mention:(\d+)\}/g, function (match, indexStr) {
    var idx = parseInt(indexStr, 10);
    var m = musicMentions[idx];
    if (!m) return match;
    return buildPillText(m);
  });
}

/**
 * Convert server mention objects to edit state objects (with pillText).
 */
export function mentionsToEditState(
  musicMentions: Array<{ title: string; artistName: string; type: string; mediaId?: string | null }>
): MusicMentionData[] {
  return musicMentions.map(function (m) {
    return {
      title: m.title,
      artistName: m.artistName,
      type: m.type as 'song' | 'album' | 'artist',
      mediaId: m.mediaId,
      pillText: buildPillText(m),
    };
  });
}

/**
 * Match Slate-ordered mentions (title/artistName/type only) with
 * the full mention data from state (which has mediaId, isrc, upc, etc).
 */
export function enrichOrderedMentions(
  orderedMentions: Array<{ title: string; artistName: string; type: string }>,
  stateMentions: MusicMentionData[]
): MusicMentionData[] {
  var used: boolean[] = new Array(stateMentions.length).fill(false);
  return orderedMentions.map(function (om) {
    // Find matching mention in state by title + artistName + type
    for (var i = 0; i < stateMentions.length; i++) {
      if (
        !used[i] &&
        stateMentions[i].title === om.title &&
        stateMentions[i].artistName === om.artistName &&
        stateMentions[i].type === om.type
      ) {
        used[i] = true;
        return stateMentions[i];
      }
    }
    // Fallback: return what we have (artist mentions don't need extra data)
    return {
      title: om.title,
      artistName: om.artistName,
      type: om.type as 'song' | 'album' | 'artist',
    };
  });
}

/**
 * Resolve music mentions for API submission: create media records for
 * song/album mentions, pass through artist mentions as-is.
 * Returns a Promise that resolves with the SubmitMention array.
 */
export function resolveMusicMentions(musicMentions: MusicMentionData[]): Promise<SubmitMention[]> {
  if (musicMentions.length === 0) {
    return Promise.resolve([]);
  }

  var promises = musicMentions.map(function (mention, idx): Promise<SubmitMention | null> {
    if (mention.type === 'artist') {
      return Promise.resolve({
        title: mention.title,
        artistName: mention.artistName,
        type: 'artist',
        placeholderIndex: idx,
      });
    }

    // If we already have a mediaId (editing an existing mention), skip media creation
    if (mention.mediaId) {
      return Promise.resolve({
        mediaId: mention.mediaId,
        title: mention.title,
        artistName: mention.artistName,
        type: mention.type,
        placeholderIndex: idx,
      });
    }

    var mediaData: any = {
      artist: mention.artistName,
      artwork_url: mention.artworkUrl || '',
      type: mention.type,
      isrc: mention.isrc,
      upc: mention.upc,
      release_date: mention.releaseDate || null,
      apple_artist_id: mention.appleArtistId || null,
    };
    if (mention.type === 'song') {
      mediaData.song_title = mention.title;
    } else {
      mediaData.album_title = mention.title;
    }

    return api
      .post('/api/media', mediaData)
      .then(function (res: any) {
        return {
          mediaId: res.data.id,
          title: mention.title,
          artistName: mention.artistName,
          type: mention.type,
          placeholderIndex: idx,
        } as SubmitMention;
      })
      .catch(function () {
        return null;
      });
  });

  return Promise.all(promises).then(function (results) {
    return results.filter(Boolean) as SubmitMention[];
  });
}
